import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 24
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const PRINCIPLES = [{
  en: 'Identity before Intelligence',
  fr: "L'identité avant l'intelligence",
  dEn: 'AI should know who it is serving.',
  dFr: "L'IA doit savoir qui elle sert."
}, {
  en: 'Verification before Response',
  fr: 'La vérification avant la réponse',
  dEn: 'Every response is validated before it reaches citizens.',
  dFr: "Chaque réponse est validée avant d'atteindre les citoyens."
}, {
  en: 'Transparency before Automation',
  fr: "La transparence avant l'automatisation",
  dEn: 'People should understand where answers come from.',
  dFr: "Chacun doit comprendre d'où viennent les réponses."
}, {
  en: 'Execution with Accountability',
  fr: "L'exécution avec responsabilité",
  dEn: 'AI should help people act safely — not just answer questions.',
  dFr: "L'IA doit aider à agir en toute sûreté — pas seulement répondre."
}, {
  en: 'Human Oversight',
  fr: 'La supervision humaine',
  dEn: 'Humans remain responsible for important decisions.',
  dFr: 'Les humains demeurent responsables des décisions importantes.'
}];
const FLOW = [{
  en: 'Citizen question',
  fr: 'Question citoyenne'
}, {
  en: 'Identity',
  fr: 'Identité'
}, {
  en: 'Municipal rules',
  fr: 'Règles municipales'
}, {
  en: 'Knowledge base',
  fr: 'Base de connaissances'
}, {
  en: 'Connected systems',
  fr: 'Systèmes connectés'
}, {
  en: 'Live municipal data',
  fr: 'Données municipales en direct'
}, {
  en: 'Confidence score',
  fr: 'Indice de confiance',
  accent: true
}, {
  en: 'ATNA verification',
  fr: 'Vérification ATNA',
  accent: true
}, {
  en: 'Verified recommendation',
  fr: 'Recommandation vérifiée'
}, {
  en: 'Execution',
  fr: 'Exécution'
}, {
  en: 'Audit trail',
  fr: "Piste d'audit",
  accent: true
}];
const STACK = [{
  en: 'Execution',
  fr: 'Exécution'
}, {
  en: 'Verified Intelligence',
  fr: 'Intelligence vérifiée'
}, {
  en: 'Policies',
  fr: 'Politiques'
}, {
  en: 'Municipal Knowledge',
  fr: 'Connaissances municipales'
}, {
  en: 'Connected Systems',
  fr: 'Systèmes connectés'
}, {
  en: 'Verified Identity',
  fr: 'Identité vérifiée',
  base: true
}];
const TRACE = [{
  en: 'Source',
  fr: 'Source'
}, {
  en: 'Regulation',
  fr: 'Réglementation'
}, {
  en: 'Knowledge article',
  fr: 'Article de connaissance'
}, {
  en: 'API consulted',
  fr: 'API consultée'
}, {
  en: 'Last update',
  fr: 'Dernière mise à jour'
}, {
  en: 'Confidence level',
  fr: 'Indice de confiance'
}, {
  en: 'Reasoning summary',
  fr: 'Résumé du raisonnement'
}];
const SECURITY = [{
  en: 'Canadian hosting',
  fr: 'Hébergement canadien'
}, {
  en: 'Privacy by design',
  fr: 'Confidentialité dès la conception'
}, {
  en: 'Law 25 & GDPR',
  fr: 'Loi 25 et RGPD'
}, {
  en: 'Consent management',
  fr: 'Gestion du consentement'
}, {
  en: 'Verified identity',
  fr: 'Identité vérifiée'
}, {
  en: 'SSO & 2FA',
  fr: 'Authentification unique et 2FA'
}, {
  en: 'Role-based access',
  fr: 'Accès basé sur les rôles'
}, {
  en: 'Audit trail',
  fr: "Piste d'audit"
}, {
  en: 'API security',
  fr: 'Sécurité des API'
}];
const GOVERNANCE = [{
  en: 'Policy enforcement',
  fr: 'Application des politiques',
  dEn: 'Municipal rules are enforced at the point of answer.',
  dFr: 'Les règles municipales sont appliquées au moment de la réponse.'
}, {
  en: 'Human review',
  fr: 'Revue humaine',
  dEn: 'Sensitive decisions are routed to a person.',
  dFr: 'Les décisions sensibles sont dirigées vers une personne.'
}, {
  en: 'Confidence thresholds',
  fr: 'Seuils de confiance',
  dEn: 'Low-confidence answers are held back, not guessed.',
  dFr: 'Les réponses à faible confiance sont retenues, pas devinées.'
}, {
  en: 'Continuous monitoring',
  fr: 'Surveillance continue',
  dEn: 'Every interaction is logged and reviewable.',
  dFr: 'Chaque interaction est journalisée et révisable.'
}];
const COMMITMENTS = [{
  en: 'Grounding every answer',
  fr: 'Ancrer chaque réponse'
}, {
  en: 'Reducing hallucinations',
  fr: 'Réduire les hallucinations'
}, {
  en: 'Protecting citizen privacy',
  fr: 'Protéger la vie privée des citoyens'
}, {
  en: 'Keeping humans accountable',
  fr: 'Garder les humains responsables'
}, {
  en: 'Improving continuously',
  fr: "S'améliorer continuellement"
}];
export function Trust() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Trust'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2747">
      <Header data-id="element-2748" />
      <main className="pt-[120px]" data-id="element-2749">
        {/* 1 — HERO */}
        <section className="pt-28 pb-28 px-6" data-id="element-2750">
          <div className="max-w-4xl mx-auto" data-id="element-2751">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-10" data-id="element-2752">
              {isFR ? 'CONFIANCE' : 'TRUST'}
              <span className="ml-3 text-[#5B6478] normal-case tracking-normal font-normal" data-id="element-2753">
                {isFR ? '· Comment BCITI bâtit une intelligence de confiance' : '· How BCITI builds trusted intelligence'}
              </span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#121212] leading-[1.1] mb-10" data-id="element-2754">
              {isFR ? <>
                  La confiance n'est pas une fonctionnalité.
                  <br data-id="element-2755" />
                  <span className="text-[#4F46E5]" data-id="element-2756">C'est la fondation.</span>
                </> : <>
                  Trust isn't a feature.
                  <br data-id="element-2757" />
                  <span className="text-[#4F46E5]" data-id="element-2758">It's the foundation.</span>
                </>}
            </motion.h1>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="max-w-2xl" data-id="element-2759">
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-2" data-id="element-2760">
                {isFR ? 'Chaque recommandation. Chaque réponse. Chaque action.' : 'Every recommendation. Every answer. Every action.'}
              </p>
              <p className="font-sans text-xl text-[#121212] leading-relaxed mb-10" data-id="element-2761">
                {isFR ? "Bâties sur l'identité vérifiée, des données de confiance et une IA responsable." : 'Built on verified identity, trusted data and accountable AI.'}
              </p>
              <a href="#framework" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-2762">
                {isFR ? 'Explorer notre cadre de confiance' : 'Explore our Trust Framework'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-2763" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* 2 — WHY TRUST MATTERS */}
        <section id="framework" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2764">
          <div className="max-w-3xl mx-auto" data-id="element-2765">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-10" data-id="element-2766">
              {isFR ? 'Pourquoi la confiance compte' : 'Why trust matters'}
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="space-y-2 mb-12" data-id="element-2767">
              <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-2768">
                {isFR ? "L'IA peut répondre." : 'AI can answer.'}
              </p>
              <p className="font-serif text-3xl md:text-4xl text-[#4F46E5] leading-snug" data-id="element-2769">
                {isFR ? 'Mais peut-on se fier à la réponse\u00a0?' : 'But can you trust the answer?'}
              </p>
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="max-w-2xl font-sans text-lg text-[#121212]/70 leading-relaxed space-y-4" data-id="element-2770">
              <p data-id="element-2771">
                {isFR ? "Les organisations publiques ont besoin de plus que des modèles puissants. Elles ont besoin d'information vérifiée, d'un raisonnement transparent, d'une exécution sécurisée et d'une responsabilité humaine." : 'Public organizations need more than powerful models. They need verified information, transparent reasoning, secure execution and human accountability.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]" data-id="element-2772">
                {isFR ? "C'est ce que BCITI a été conçu pour livrer." : 'That is what BCITI was built to deliver.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* 3 — PRINCIPLES */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2773">
          <div className="max-w-4xl mx-auto" data-id="element-2774">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-16" data-id="element-2775">
              {isFR ? 'Nos principes' : 'Our principles'}
            </motion.div>
            <div className="divide-y divide-[#E4E6EC] border-y border-[#E4E6EC]" data-id="element-2776">
              {PRINCIPLES.map((p, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.6,
              delay: i * 0.06
            }} className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-12 py-8" data-id="element-2777">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#121212] leading-tight" data-id="element-2778">
                    {isFR ? p.fr : p.en}
                  </h3>
                  <p className="font-sans text-lg text-[#5B6478] leading-relaxed md:pt-1" data-id="element-2779">
                    {isFR ? p.dFr : p.dEn}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 4 — ATNA VERIFICATION ENGINE (flow) */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2780">
          <div className="max-w-3xl mx-auto" data-id="element-2781">
            <div className="text-center mb-16" data-id="element-2782">
              <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-2783">
                {isFR ? 'Le moteur de vérification ATNA' : 'The ATNA Verification Engine'}
              </motion.div>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-lg text-[#5B6478] max-w-2xl mx-auto" data-id="element-2784">
                {isFR ? 'Une IA générique va de la question à la réponse. ATNA passe par la vérification.' : 'Generic AI goes from question to answer. ATNA goes through verification.'}
              </motion.p>
            </div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="max-w-md mx-auto" data-id="element-2785">
              <>
                <img src={isFR ? 'https://cdn.magicpatterns.com/uploads/9BwFZxbQKAqnkEoeXJfSPg/ChatGPT_Image_7_juil._2026,_16_19_09.png' : 'https://cdn.magicpatterns.com/uploads/nnshidKdZb2uHZBKHPDER2/ChatGPT_Image_7_juil._2026,_16_20_14.png'} alt={isFR ? "Le moteur de vérification ATNA : sources vérifiées alimentant une étiquette de vérification (grade, indice, axes), recommandation vérifiée, exécution avec autorisation requise et piste d'audit." : 'The ATNA Verification Engine: verified sources feeding a verification label (grade, index, axes), verified recommendation, execution with authorization required, and audit trail.'} className="w-full h-auto object-contain" data-id="element-2786" />
                <p className="text-center font-sans text-[11px] text-[#5B6478] mt-4" data-id="element-2787">
                  {isFR ? 'Démonstration · valeurs illustratives' : 'Demonstration · illustrative values'}
                </p>
              </>
            </motion.div>
          </div>
        </section>

        {/* 5 — TRUST STACK */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2788">
          <div className="max-w-3xl mx-auto" data-id="element-2789">
            <div className="text-center mb-16" data-id="element-2790">
              <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] mb-6" data-id="element-2791">
                {isFR ? 'La Trust Stack' : 'The Trust Stack'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-lg text-[#5B6478]" data-id="element-2792">
                {isFR ? 'Chaque couche renforce la suivante.' : 'Every layer reinforces the next.'}
              </motion.p>
            </div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="border border-[#E4E6EC] divide-y divide-[#E4E6EC] max-w-xl mx-auto" data-id="element-2793">
              {STACK.map((layer, i) => <div key={i} className={`px-8 py-6 text-center font-serif text-xl ${layer.base ? 'bg-[#4F46E5]/[0.04] text-[#4F46E5]' : 'bg-white text-[#121212]'}`} data-id="element-2794">
                  {isFR ? layer.fr : layer.en}
                </div>)}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-center font-sans text-sm text-[#5B6478] mt-6" data-id="element-2795">
              {isFR ? 'Trust by architecture — pas trust by policy.' : 'Trust by architecture — not trust by policy.'}
            </motion.p>
          </div>
        </section>

        {/* 6 — EXPLAINABILITY (design intent) */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2796">
          <div className="max-w-3xl mx-auto" data-id="element-2797">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-8" data-id="element-2798">
              {isFR ? 'Explicabilité' : 'Explainability'}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-12 max-w-2xl" data-id="element-2799">
              {isFR ? 'ATNA est conçu pour que chaque réponse puisse être tracée jusqu’à\u00a0:' : 'ATNA is designed so every answer can be traced to:'}
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2800">
              {TRACE.map((item, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="flex items-center gap-3 border border-[#E4E6EC] bg-white px-5 py-4" data-id="element-2801">
                  <CheckIcon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={2.25} data-id="element-2802" />
                  <span className="font-sans text-base text-[#121212]" data-id="element-2803">
                    {isFR ? item.fr : item.en}
                  </span>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 7 — SECURITY & PRIVACY */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2804">
          <div className="max-w-5xl mx-auto" data-id="element-2805">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] mb-16 text-center" data-id="element-2806">
              {isFR ? 'Sécurité et confidentialité' : 'Security & Privacy'}
            </motion.h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-2807">
              {SECURITY.map((card, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.04
            }} className="bg-white p-8 flex items-center" data-id="element-2808">
                  <span className="font-serif text-lg md:text-xl text-[#121212] leading-snug" data-id="element-2809">
                    {isFR ? card.fr : card.en}
                  </span>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 8 — AI GOVERNANCE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2810">
          <div className="max-w-4xl mx-auto" data-id="element-2811">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug mb-4" data-id="element-2812">
              {isFR ? <>
                  ATNA a été conçu autour de la gouvernance.
                  <br data-id="element-2813" />
                  <span className="text-[#5B6478]" data-id="element-2814">
                    Pas ajoutée après coup.
                  </span>
                </> : <>
                  ATNA was designed around governance.
                  <br data-id="element-2815" />
                  <span className="text-[#5B6478]" data-id="element-2816">Not added afterwards.</span>
                </>}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mt-12" data-id="element-2817">
              {GOVERNANCE.map((g, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className="bg-white p-8" data-id="element-2818">
                  <h3 className="font-serif text-xl text-[#121212] mb-2" data-id="element-2819">
                    {isFR ? g.fr : g.en}
                  </h3>
                  <p className="font-sans text-base text-[#5B6478] leading-relaxed" data-id="element-2820">
                    {isFR ? g.dFr : g.dEn}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 9 — RESPONSIBLE AI */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2821">
          <div className="max-w-3xl mx-auto" data-id="element-2822">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-8" data-id="element-2823">
              {isFR ? 'IA responsable' : 'Responsible AI'}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-2xl md:text-3xl text-[#121212] mb-10" data-id="element-2824">
              {isFR ? 'Nous nous engageons à\u00a0:' : 'We commit to:'}
            </motion.p>
            <div className="space-y-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-2825">
              {COMMITMENTS.map((c, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="bg-white px-8 py-6 flex items-center gap-4" data-id="element-2826">
                  <CheckIcon className="w-5 h-5 text-[#4F46E5] shrink-0" strokeWidth={2} data-id="element-2827" />
                  <span className="font-sans text-lg text-[#121212]" data-id="element-2828">
                    {isFR ? c.fr : c.en}
                  </span>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 10 — TRUST BY DESIGN + CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2829">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2830">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-[#121212] leading-[1.2] mb-14" data-id="element-2831">
              {isFR ? "La confiance ne s'ajoute pas après le déploiement. Elle est conçue dans chaque interaction." : "Trust isn't something added after deployment. It is designed into every interaction."}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} data-id="element-2832">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Trust Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2833">
                {isFR ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2834" />
              </Link>
              <p className="mt-5 font-sans text-sm text-[#5B6478]" data-id="element-2835">
                {isFR ? 'Voyez comment fonctionne une IA municipale de confiance.' : 'See how trusted municipal AI works.'}
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer data-id="element-2836" />
    </div>;
}